import React, { useCallback, useEffect } from "react";
import { commitMutation, graphql, type PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { faChevronDown, faChevronUp, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { noop } from "lodash";

import useToggle from "~/lib/hooks/useToggle";
import { getAllMarks } from "~/lib/prose/marks";
import {
	BulletPoint,
	CustomEmoji,
	Embed,
	HardBreak,
	Header,
	HorizontalRule,
	KeyResult,
	Mention,
	NumberedListItem,
	Tag,
	TextNode,
	Unstyled,
} from "~/lib/prose/nodes";
import { ImageStrip, ImageStripItem, ImageStripItemCaption, ImageStripItemFile } from "~/lib/prose/nodes/image-strip";
import { Agenda, Goal, Indent, Todo } from "~/lib/prose/plugins";
import BlockBoundary from "~/lib/prose/plugins/blockBoundary";
import { EditorConfig, ProseReact } from "~/lib/prose/prose-react";
import { formatTimestamp } from "~/meetingRecorder/utils";
import { environment } from "~/models";
import { Avatar, Button } from "~/ui";
import type { AvatarUserAttrs } from "~/ui/avatar";
import { HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { ReactComponent as CommentIllustration } from "./images/comment.svg";
import { Comment, reopenThreadMutation } from "./index";

import type { CommentsReopenThreadMutation } from "~/__generated__/CommentsReopenThreadMutation.graphql";
import type { ResolvedModalQuery } from "~/__generated__/ResolvedModalQuery.graphql";

import styles from "./css/Comments.module.css";

const resolvedModalQuery = graphql`
	query ResolvedModalQuery($id: ID!) {
		node(id: $id) {
			... on Note {
				rawDraftJson
				resolvedComments {
					threadKey
					segment

					threads {
						id
						key
						resolvedAt
						resolvedBy {
							id
							fullName
							image
							isGuest
							isTopUser
							isExternal
						}

						comments {
							id
							createdAt
							editedAt
							isDeleted
							text
							html
							type

							user {
								id
								fullName
								image
								isGuest
								isTopUser
								isExternal
							}
						}
					}
				}
			}
		}

		viewer {
			user {
				id
				isGuest
				account {
					id
				}
			}
		}
	}
`;

type ResolvedModalProps = {
	noteId: string;
	onClose: () => void;
};

type Threads = NonNullable<NonNullable<ResolvedModalQuery["response"]["node"]>["resolvedComments"]>;
type NoteContent = NonNullable<NonNullable<ResolvedModalQuery["response"]["node"]>["rawDraftJson"]>;

type ResolvedModalInternalProps = {
	query: PreloadedQuery<ResolvedModalQuery>;
	reopenThread(threadId: string): void;
	loadingThreadId: string;
};

type BlockThreadsProps = {
	threads: Threads[0];
	noteContent: NoteContent;
	config: EditorConfig;
};

type ThreadCommentsProps = {
	thread: Threads[0]["threads"][0];
};

type ResolvedModalContextProps = {
	currentUser?: ResolvedModalQuery["response"]["viewer"];
	reopenThread: (threadId: string) => void;
	loadingThreadId: string | null;
};

const resolvedModalContextDefault = {
	currentUser: undefined,
	loadingThreadId: null,
	reopenThread: noop,
};

const ResolvedModalContext = React.createContext<ResolvedModalContextProps>(resolvedModalContextDefault);
ResolvedModalContext.displayName = "ResolvedModalContext";

function ThreadComments({ thread }: ThreadCommentsProps) {
	const [expanded, toggleExpanded] = useToggle(false);
	const { currentUser, reopenThread, loadingThreadId } = React.useContext(ResolvedModalContext);

	const firstComment = thread.comments.length > 0 ? thread.comments[0] : null;

	const reopenClicked = React.useCallback(() => {
		reopenThread(thread.id);
	}, [thread, reopenThread]);

	const moreCommentsCount = thread.comments.length - 1;

	return (
		<div className={styles.thread} key={`thread-${thread.key}`}>
			{!expanded && firstComment ? (
				<Comment readOnly comment={firstComment} currentUser={currentUser} threadKey={thread.key} />
			) : (
				thread.comments.map(comment => (
					<Comment
						readOnly
						key={`comment-${comment.id}`}
						comment={comment}
						currentUser={currentUser}
						threadKey={thread.key}
					/>
				))
			)}

			<div className={styles.moreCommentsLabel} onClick={toggleExpanded}>
				{expanded ? (
					<>
						Show less <FontAwesomeIcon icon={faChevronUp} />
					</>
				) : moreCommentsCount > 0 ? (
					<>
						Show more ({moreCommentsCount}) <FontAwesomeIcon icon={faChevronDown} />
					</>
				) : null}
			</div>

			{thread.resolvedAt && (
				<div className={styles.buttonCrossWrapper}>
					<Button
						size="slim"
						variant="primary"
						disabled={loadingThreadId === thread.id}
						className={styles.resolveButton}
						onClick={reopenClicked}
					>
						Reopen
					</Button>

					{thread.resolvedBy && (
						<Comment
							readOnly
							threadKey={thread.key}
							comment={{
								id: "fake-id",
								editedAt: null,
								text: "",
								html: "",
								type: "RESOLVE",
								isDeleted: false,
								user: thread.resolvedBy,
								createdAt: thread.resolvedAt,
							}}
						/>
					)}
				</div>
			)}
		</div>
	);
}

function BlockThreads({ threads, noteContent, config }: BlockThreadsProps) {
	const segment = threads.segment;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const item = noteContent.doc.content.find((i: any) => i.attrs.blockId === threads.threadKey);
	let blockContent;

	if (item) {
		const doc = { type: "doc", content: [item] };

		blockContent = <ProseReact editable={false} config={config} initialDoc={doc} enableReactHost />;
	} else if (segment) {
		const avatarUser: AvatarUserAttrs = { fullName: segment.speaker };

		blockContent = (
			<div>
				<div className={styles.transcriptSegmentHeader}>
					<Avatar user={avatarUser} size="small" />

					<div className={styles.transcriptSegmentSpeaker}>{segment.speaker}</div>

					{segment.words && segment.words.length > 0 && (
						<div className={styles.transcriptSegmentTimestamp}>
							{formatTimestamp(segment.words[0].start_timestamp, null)}
						</div>
					)}
				</div>

				{segment.words && segment.words.length > 0 && (
					<div className={styles.transcriptSegmentText}>{segment.words[0].text}</div>
				)}
			</div>
		);
	} else {
		blockContent = (
			<div className={styles.deletedItem}>
				<FontAwesomeIcon className={styles.icon} icon={faTrashCan} />
				<div>This text was deleted</div>
			</div>
		);
	}

	return (
		<div className={styles.resolvedBlockWrapper}>
			<div className={styles.resolvedBlockItem}>{blockContent}</div>

			{threads.threads.map(t => (
				<ThreadComments key={`thread-${t.id}`} thread={t} />
			))}
		</div>
	);
}

function ResolvedModalInternal({ query, reopenThread, loadingThreadId }: ResolvedModalInternalProps) {
	const info = usePreloadedQuery<ResolvedModalQuery>(resolvedModalQuery, query);
	const threads = info.node?.resolvedComments;
	const { viewer } = info;

	const config = React.useMemo(
		() =>
			new EditorConfig({
				nodes: {
					doc: {
						content: "block+",
					},
				},
				extensions: [
					...getAllMarks(),
					new BlockBoundary(),
					new TextNode(),
					new Header(),
					new Unstyled(),
					new BulletPoint(),
					new NumberedListItem({ hideListItemNumber: true }),
					new Mention({ currentUserId: viewer.user.id }),
					new HardBreak(),
					new HorizontalRule(),
					new Indent(),
					new Tag({ showToolbar: false, clickable: !viewer.user.isGuest }),
					new Todo({ disabledReadOnly: true, viewerId: viewer.user.id }),
					new Agenda({ disabledReadOnly: true, viewerId: viewer.user.id }),
					new Goal({ viewerId: viewer.user.id }),
					new ImageStrip({ contentId: "", disableWhenNotEditable: true }),
					new ImageStripItem(),
					new ImageStripItemFile(),
					new ImageStripItemCaption(),
					new Embed(),
					new KeyResult(),
					new CustomEmoji(),
				],
			}),
		[], // eslint-disable-line react-hooks/exhaustive-deps
		// Changing the editorconfig has no effect
	);

	const contextValue = React.useMemo(
		() => ({
			currentUser: viewer,
			reopenThread,
			loadingThreadId,
		}),
		[viewer, reopenThread, loadingThreadId],
	);

	if (!threads || threads.length === 0) {
		return (
			<div className={styles.noCommentsBody}>
				<CommentIllustration />

				<div className={styles.noCommentsLabel}>No resolved comments</div>
			</div>
		);
	}

	return (
		<ResolvedModalContext.Provider value={contextValue}>
			{threads.map(t => (
				<BlockThreads
					threads={t}
					config={config}
					noteContent={info.node?.rawDraftJson}
					key={`threads-${t.threadKey}`}
				/>
			))}
		</ResolvedModalContext.Provider>
	);
}

export default function ResolvedModal(props: ResolvedModalProps) {
	const { noteId, onClose } = props;
	const [loadingThreadId, setLoadingThreadId] = React.useState("");
	const [queryRef, loadQuery] = useQueryLoader<ResolvedModalQuery>(resolvedModalQuery);

	const retry = useCallback(() => {
		loadQuery({ id: noteId }, { fetchPolicy: "store-and-network" });
	}, [noteId, loadQuery]);

	useEffect(() => {
		retry();
	}, [retry]);

	const reopenThread = React.useCallback(
		(threadId: string) => {
			setLoadingThreadId(threadId);
			commitMutation<CommentsReopenThreadMutation>(environment, {
				mutation: reopenThreadMutation,
				variables: { id: threadId },
				onCompleted: () => {
					setLoadingThreadId("");
					retry();
				},
			});
		},
		[retry],
	);

	return (
		<Modal onClose={onClose} className={styles.resolvedModal}>
			<Modal.Header>
				<Modal.Title>Resolved comments</Modal.Title>
				<Modal.HeaderActions onClose={onClose}>
					<HelpCenterIcon
						articleId="5301276"
						title="Comments & reactions"
						className={styles.resolvedModalQuestionIcon}
					/>
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body>
				{queryRef ? (
					<React.Suspense fallback={<LoadingSpinner inline />}>
						<ResolvedModalInternal query={queryRef} reopenThread={reopenThread} loadingThreadId={loadingThreadId} />
					</React.Suspense>
				) : (
					<LoadingSpinner inline />
				)}
			</Modal.Body>
		</Modal>
	);
}
